import ReactPlayer, { ReactPlayerProps } from "react-player";
import { useState, useRef, useEffect, useCallback } from "react";
import {
  BsPlayFill,
  BsPauseFill,
  BsVolumeMuteFill,
  BsVolumeUpFill,
} from "react-icons/bs";
import { MdPictureInPictureAlt } from "react-icons/md";
import { RxEnterFullScreen } from "react-icons/rx";
import Slider from "@mui/material/Slider";
import CircularProgress from "@mui/material/CircularProgress";
import * as S from "./styles";

type CustomPlayerProps = ReactPlayerProps;

function CustomPlayer({ ...props }: CustomPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeControl, setShowVolumeControl] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isInteractingWithControls, setIsInteractingWithControls] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const playerRef = useRef<ReactPlayer>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const volumeControlRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<number | null>(null);

  // Função para exibir os controles e iniciar o temporizador para escondê-los
  const handleShowControls = useCallback(() => {
    setShowControls(true);
    setShowCursor(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying && !isInteractingWithControls) {
        setShowControls(false);
        setShowCursor(false);
      }
    }, 3000);
  }, [isPlaying, isInteractingWithControls]);

  // Iniciar interação com os controles
  const handleControlsInteractionStart = () => {
    setIsInteractingWithControls(true);
    setShowControls(true);
    setShowCursor(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
  };

  // Finalizar interação com os controles
  const handleControlsInteractionEnd = useCallback(() => {
    setIsInteractingWithControls(false);
    handleShowControls();
  },[handleShowControls]);

  // Efeito para configurar eventos de movimento do mouse e clique
  useEffect(() => {
    const playerContainer = containerRef.current;
    const controlsContainer = controlsRef.current;
    if (!playerContainer || !controlsContainer) return;
    const handleMouseMove = () => handleShowControls();
    const handleClick = () => handleShowControls();
    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as Node;
      if (controlsContainer.contains(target)) {
        handleControlsInteractionStart();
      } else {
        handleShowControls();
      }
    };
    const handleTouchEnd = (e: TouchEvent) => {
      const target = e.target as Node;
      if (controlsContainer.contains(target)) {
        handleControlsInteractionEnd();
      }
    };
    const handleMouseEnter = () => {
      handleShowControls();
    };
    const handleMouseLeave = () => {
      setShowCursor(true);
      if (isPlaying) {
        setShowControls(false);
      }
    };
    playerContainer.addEventListener('mousemove', handleMouseMove);
    playerContainer.addEventListener('click', handleClick);
    playerContainer.addEventListener('touchstart', handleTouchStart);
    playerContainer.addEventListener('touchend', handleTouchEnd);
    playerContainer.addEventListener('mouseenter', handleMouseEnter);
    playerContainer.addEventListener('mouseleave', handleMouseLeave);
    controlsContainer.addEventListener('mouseenter', handleControlsInteractionStart);
    controlsContainer.addEventListener('mouseleave', handleControlsInteractionEnd);
    handleShowControls();
    return () => {
      playerContainer.removeEventListener('mousemove', handleMouseMove);
      playerContainer.removeEventListener('click', handleClick);
      playerContainer.removeEventListener('touchstart', handleTouchStart);
      playerContainer.removeEventListener('touchend', handleTouchEnd);
      playerContainer.removeEventListener('mouseenter', handleMouseEnter);
      playerContainer.removeEventListener('mouseleave', handleMouseLeave);
      controlsContainer.removeEventListener('mouseenter', handleControlsInteractionStart);
      controlsContainer.removeEventListener('mouseleave', handleControlsInteractionEnd);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [handleControlsInteractionEnd, handleShowControls, isPlaying]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    handleShowControls();
  };

  const handleMute = () => {
    if (!isMuted) {
      setVolume(0);
      setIsMuted(true);
      return;
    }
    setVolume(1);
    setIsMuted(false);
  };

  const handleVolumeChange = (value: number) => {
    setVolume(value);
    if (value === 0) {
      setIsMuted(true);
      return;
    }
    setIsMuted(false);
  };

  const togglePictureInPicture = async () => {
    if (playerRef.current && !document.pictureInPictureElement) {
      try {
        await playerRef.current.getInternalPlayer().requestPictureInPicture();
      } catch (error) {
        console.error("Picture-in-Picture failed:", error);
      }
    } else if (document.pictureInPictureElement) {
      await document.exitPictureInPicture();
    }
  };

  const handleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  // Esconder o controle de volume ao clicar fora dele
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        volumeControlRef.current &&
        !volumeControlRef.current.contains(event.target as Node) &&
        showVolumeControl
      ) {
        setShowVolumeControl(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showVolumeControl]);

  const handleFirstPlay = () => {
    setIsPlaying(true);
    setIsFirstLoad(false);
    setIsLoading(false);
    handleShowControls();
  };

  // Handlers de loading/buffering do ReactPlayer
  const handleBuffer = () => {
    setIsBuffering(true);
    setIsLoading(true);
  };
  const handleBufferEnd = () => {
    setIsBuffering(false);
    setIsLoading(false);
  };
  const handleReady = () => {
    setIsLoading(false);
  };
  const handleStart = () => {
    setIsLoading(false);
  };
  const handlePlay = () => {
    setIsLoading(false);
  };
  const handlePause = () => {
    setIsLoading(false);
  };
  const handleError = () => {
    setIsLoading(false);
  };

  return (
    <S.PlayerContainer
      ref={containerRef}
      showCursor={showCursor}
    >
      <ReactPlayer
        ref={playerRef}
        url={props.url}
        width="100%"
        height="100%"
        playing={isPlaying}
        muted={isMuted}
        volume={volume}
        controls={false}
        onBuffer={handleBuffer}
        onBufferEnd={handleBufferEnd}
        onReady={handleReady}
        onStart={handleStart}
        onPlay={handlePlay}
        onPause={handlePause}
        onError={handleError}
        {...props}
      />

      {/* Spinner de carregamento/buffering */}
      {(isLoading || isBuffering) && !isFirstLoad && (
        <S.LoadingOverlay>
          <CircularProgress color="success" size={64} />
        </S.LoadingOverlay>
      )}

      {/* Botão de Play central para primeira carga */}
      {isFirstLoad && (
        <S.FirstLoadOverlay>
          <S.PlayButton
            onClick={handleFirstPlay}
            aria-label="Iniciar reprodução"
          >
            <BsPlayFill />
          </S.PlayButton>
        </S.FirstLoadOverlay>
      )}

      {/* Controles customizados */}
      <S.ControlsContainer
        ref={controlsRef}
        showControls={showControls}
        onTouchStart={handleControlsInteractionStart}
        onTouchEnd={handleControlsInteractionEnd}
      >
        {/* Botão Play/Pause */}
        <S.ControlButton onClick={handlePlayPause}>
          {isPlaying ? <BsPauseFill /> : <BsPlayFill />}
        </S.ControlButton>

        {/* Controle de Volume - oculto em telas móveis */}
        <S.VolumeControl
          ref={volumeControlRef}
          onMouseEnter={() => setShowVolumeControl(true)}
          onMouseLeave={() => setShowVolumeControl(false)}
        >
          <S.ControlButton onClick={handleMute}>
            {isMuted || volume === 0 ? (
              <BsVolumeMuteFill />
            ) : (
              <BsVolumeUpFill />
            )}
          </S.ControlButton>

          {/* Slider de Volume */}
          <S.VolumeSlider>
            <Slider
              min={0}
              max={1}
              step={0.01}
              defaultValue={1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
              value={volume}
              onChange={(_, value) => handleVolumeChange(value as number)}
              sx={{color:"#25b907"}}
            />
          </S.VolumeSlider>
        </S.VolumeControl>

        {/* Indicador LIVE */}
        <S.LiveIndicator>
          <S.LiveDot />
          <S.LiveText>Ao Vivo</S.LiveText>
        </S.LiveIndicator>

        {/* Espaçador Flexível */}
        <S.FlexSpacer />

        {/* Botão PIP */}
        <S.ControlButton onClick={togglePictureInPicture}>
          <MdPictureInPictureAlt />
        </S.ControlButton>

        {/* Botão Tela Cheia */}
        <S.ControlButton onClick={handleFullscreen}>
          <RxEnterFullScreen />
        </S.ControlButton>
      </S.ControlsContainer>
    </S.PlayerContainer>
  );
}

export default CustomPlayer; 